import React from 'react';
import HeaderFooterLayout from '../components/HeaderFooterLayout';
import { ShieldCheck } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
      {title}
    </h2>
    <div className="text-gray-600 dark:text-gray-400 space-y-3 leading-relaxed">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <HeaderFooterLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-12">
            <ShieldCheck className="mx-auto h-12 w-12 text-neutral-700 dark:text-gray-300 mb-4" />
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Privacy Policy</h1>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Last Updated: November 2025</p>
          </div>

          <div className="space-y-10">
            <Section title="1. Overview">
              <p>
                This Privacy Policy describes how <strong>Lumosye Health Services, LLC</strong> (“we,” “our,” or “us”) collects,
                uses, and protects information across our digital platforms, including <strong>LumoChart</strong>,
                <strong> LumoDx</strong>, and related web services. By using our products, you agree to the terms described here.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 !mt-2">A. Account Information</h3>
              <p>We collect professional details when creating accounts, such as:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Full name, email, and credentials (e.g., MD, NP, PA-C)</li>
                <li>Auth tokens via Google, Apple, or email sign-in</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 pt-4">B. Clinical Data</h3>
              <p>When using LumoChart, you may enter clinical content with minimized PHI:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>First name, last name, age, and sex</li>
                <li>Non-permanent identifiers (room/bed number)</li>
                <li>Generated or transcribed note content</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 pt-4">C. What We Never Collect</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>MRNs, addresses, phone numbers, or SSNs</li>
                <li>Insurance or billing identifiers</li>
              </ul>
            </Section>

            <Section title="3. How We Use Information">
              <ul className="list-disc pl-5 space-y-2">
                <li>Authenticate users securely</li>
                <li>Store and sync notes</li>
                <li>Provide AI tools for documentation and analysis</li>
                <li>Improve reliability and design</li>
              </ul>
            </Section>

            <Section title="4. Data Security">
              <ul className="list-disc pl-5 space-y-2">
                <li>Data encrypted in transit and at rest</li>
                <li>Identity managed via Firebase Auth</li>
                <li>PHI minimization and role-based controls enforced</li>
              </ul>
            </Section>

            <Section title="5. Retention & Deletion">
              <p>
                All clinical note data is <strong>automatically deleted after 15 days</strong>.
                Users may delete notes or accounts earlier at any time.
              </p>
            </Section>

            <Section title="6. Third-Party Services">
              <p>
                We rely on secure infrastructure by <strong>Google Cloud</strong> and <strong>Apple</strong>, which comply with
                industry-standard privacy frameworks.
              </p>
            </Section>

            <Section title="7. Children’s Privacy">
              <p>Our products are for licensed healthcare professionals; no data is collected from minors.</p>
            </Section>

            <Section title="8. Policy Changes">
              <p>Updates will be posted here with a new “Last Updated” date.</p>
            </Section>

            <Section title="9. Contact Us">
              <p>
                <strong>Lumosye Health Services, LLC</strong><br />
                📧{' '}
                <a
                  href="mailto:contact@lumosyehealth.com"
                  className="text-neutral-700 dark:text-gray-200 underline hover:text-neutral-900 dark:hover:text-white"
                >
                  contact@lumosyehealth.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
}
